import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Wand2, Send, Check, Clock, Trash2, Copy, Loader2, ShieldCheck } from "lucide-react";

const CHANNELS = [
  ["courriel", "✉️ Courriel"], ["sms", "💬 SMS"], ["facebook", "📘 Facebook"],
  ["instagram", "📸 Instagram"], ["tiktok", "🎵 TikTok"], ["annonce", "📣 Annonce dojo"], ["tv", "📺 Écran TV"],
];
const TONES = ["chaleureux et motivant", "professionnel", "amical et décontracté", "énergique", "sérieux et rassurant"];

export default function StudioIAModule() {
  const [brief, setBrief] = useState("");
  const [channel, setChannel] = useState("courriel");
  const [ton, setTon] = useState(TONES[0]);
  const [audience, setAudience] = useState("membres et parents");
  const [draft, setDraft] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/accueil-pilotage/studio"); setList(data.communications || []); }
    catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!brief.trim()) { toast.error("Décris ce que tu veux communiquer."); return; }
    setBusy(true);
    try { const { data } = await api.post("/accueil-pilotage/studio/generate", { brief, channel, ton, audience }); setDraft(data.content); if (!title) setTitle(brief.slice(0, 60)); }
    catch (e) { toast.error(e.response?.data?.detail || "Génération impossible"); } setBusy(false);
  };
  const improve = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/accueil-pilotage/studio/improve", { content: draft, channel }); setDraft(data.content); toast.success("Texte amélioré ✨"); }
    catch { toast.error("Erreur"); } setBusy(false);
  };
  const saveDraft = async () => {
    if (!draft.trim()) { toast.error("Rien à enregistrer."); return; }
    setBusy(true);
    try { await api.post("/accueil-pilotage/studio", { title: title || "Communication", channel, content: draft, audience }); toast.success("Enregistré — en attente d'approbation."); setDraft(""); setBrief(""); setTitle(""); load(); }
    catch { toast.error("Erreur"); } setBusy(false);
  };
  const approve = async (c) => { try { await api.put(`/accueil-pilotage/studio/${c.id}`, { status: c.status === "approved" ? "pending_review" : "approved" }); load(); } catch { toast.error("Erreur"); } };
  const del = async (id) => { try { await api.delete(`/accueil-pilotage/studio/${id}`); load(); } catch { toast.error("Erreur"); } };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="studio-ia-page">
      <div>
        <div className="text-[#C9A961] text-xs uppercase tracking-widest">Accueil & Pilotage</div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-2"><Sparkles className="w-7 h-7 text-[#C9A961]" /> Studio IA — Communications</h1>
        <p className="text-zinc-400 text-sm mt-1">Génère et améliore tes messages. <span className="text-amber-300">Révision humaine obligatoire avant tout envoi.</span></p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Générateur */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="studio-generator">
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map(([k, l]) => (
              <button key={k} onClick={() => setChannel(k)} data-testid={`studio-chan-${k}`}
                className={`text-[11px] px-2.5 py-1 rounded-full ${channel === k ? "bg-[#C9A961] text-black font-semibold" : "bg-white/5 text-zinc-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={ton} onChange={(e) => setTon(e.target.value)} data-testid="studio-ton" className="bg-zinc-950 border border-white/10 rounded-lg px-2 py-2 text-xs text-zinc-200">
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Public cible" data-testid="studio-audience" className="bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-white" />
          </div>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4} data-testid="studio-brief"
            placeholder="Ex. Annoncer la rencontre obligatoire de rentrée le 5 septembre à 18h30…"
            className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          <button onClick={generate} disabled={busy} data-testid="studio-generate"
            className="w-full bg-[#C9A961] hover:bg-white text-black font-semibold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Générer avec l'IA
          </button>
        </div>

        {/* Brouillon éditable */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="studio-draft">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre interne de la communication" data-testid="studio-title" className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={10} data-testid="studio-content"
            placeholder="Le texte généré apparaîtra ici — tu peux l'éditer avant de l'enregistrer."
            className="w-full bg-zinc-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white whitespace-pre-line" />
          <div className="flex gap-2">
            <button onClick={improve} disabled={busy || !draft} data-testid="studio-improve" className="flex-1 text-xs py-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200 disabled:opacity-40 flex items-center justify-center gap-1"><Sparkles className="w-4 h-4" /> Améliorer</button>
            <button onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copié !"); }} disabled={!draft} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200 disabled:opacity-40"><Copy className="w-4 h-4" /></button>
            <button onClick={saveDraft} disabled={busy || !draft} data-testid="studio-save" className="flex-1 text-xs py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-40 flex items-center justify-center gap-1"><Check className="w-4 h-4" /> Enregistrer (à approuver)</button>
          </div>
        </div>
      </div>

      {/* Liste des communications */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="studio-list">
        <h2 className="text-white font-semibold mb-3">Communications ({list.length})</h2>
        {list.length === 0 ? <p className="text-zinc-500 text-sm py-4 text-center">Aucune communication enregistrée.</p> : (
          <div className="space-y-2">
            {list.map((c) => (
              <div key={c.id} className="rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid={`studio-comm-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0"><div className="text-sm text-white font-medium truncate">{c.title}</div><div className="text-[11px] text-zinc-500">{c.channel} · {c.audience || "—"}</div></div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${c.status === "approved" ? "bg-emerald-600/30 text-emerald-200" : "bg-amber-600/30 text-amber-200"}`}>
                      {c.status === "approved" ? <><ShieldCheck className="w-3 h-3" /> Approuvée</> : <><Clock className="w-3 h-3" /> À approuver</>}
                    </span>
                    <button onClick={() => approve(c)} data-testid={`studio-approve-${c.id}`} className={`text-[10px] px-2 py-1 rounded ${c.status === "approved" ? "bg-white/5 text-zinc-400" : "bg-emerald-600 text-white font-semibold"}`}>{c.status === "approved" ? "Annuler" : "Approuver"}</button>
                    <button onClick={() => del(c.id)} className="p-1 rounded bg-white/5 hover:bg-red-500/30 text-zinc-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="text-xs text-zinc-400 mt-2 whitespace-pre-line line-clamp-4">{c.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
